import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { Settings as SettingsIcon, Shield, Calendar, CheckSquare, Share2, Star, Wifi, Database, Sliders } from 'lucide-react';
import { CalendarIntegration, TasksSyncIntegration, SocialMediaIntegration, ReviewPlatformIntegration } from '../../components/settings';
import ConnectionStatusOverview from '../../components/settings/ConnectionStatusOverview';
import EmailProviderSettings from '../../components/email/EmailProviderSettings';
import UserAuthManager from '../../components/admin/UserAuthManager';
import { ChatbotAnalytics } from '../../components/chatbot';
import CRMIntegration from '../../components/settings/CRMIntegration';
import RiskNumberIntegration from '../../components/settings/RiskNumberIntegration';

const Settings: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'current-status' | 'calendar' | 'tasks' | 'email' | 'auth' | 'chatbot' | 'social' | 'reviews' | 'crm' | 'risknumber'>('current-status');

  // Check if user is super admin
  const { data: isSuperAdmin } = useQuery({
    queryKey: ['isSuperAdmin'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;

      const { data, error } = await supabase
        .from('user_organizations')
        .select('role:user_roles(level)')
        .eq('user_id', user.id)
        .eq('status', 'active')
        .single();

      if (error) return false;
      return data?.role?.level === 1;
    }
  });

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="mt-1 text-gray-600">
          Manage your account settings and external service connections
        </p>
      </div>

      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 overflow-x-auto">
            <button
              onClick={() => setActiveTab('current-status')}
              data-tab="current-status"
              className={`${
                activeTab === 'current-status'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
            >
              <Wifi className="h-5 w-5 mr-2" />
              Current Status
            </button>
            <button
              onClick={() => setActiveTab('calendar')}
              data-tab="calendar"
              className={`${
                activeTab === 'calendar'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
            >
              <Calendar className="h-5 w-5 mr-2" />
              Calendar Integration
            </button>
            <button
              onClick={() => setActiveTab('tasks')}
              data-tab="tasks"
              className={`${
                activeTab === 'tasks'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
            >
              <CheckSquare className="h-5 w-5 mr-2" />
              Tasks Sync
            </button>
            <button
              onClick={() => setActiveTab('crm')}
              data-tab="crm"
              className={`${
                activeTab === 'crm'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
            >
              <Database className="h-5 w-5 mr-2" />
              CRM Integration
            </button>
            <button
              onClick={() => setActiveTab('risknumber')}
              data-tab="risknumber"
              className={`${
                activeTab === 'risknumber'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
            >
              <Sliders className="h-5 w-5 mr-2" />
              Risk Number API
            </button>
            <button
              onClick={() => setActiveTab('social')}
              data-tab="social"
              className={`${
                activeTab === 'social'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
            >
              <Share2 className="h-5 w-5 mr-2" />
              Social Media
            </button>
            <button
              onClick={() => setActiveTab('reviews')}
              data-tab="reviews"
              className={`${
                activeTab === 'reviews'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
            >
              <Star className="h-5 w-5 mr-2" />
              Review Sync
            </button>
            <button
              onClick={() => setActiveTab('email')}
              data-tab="email"
              className={`${
                activeTab === 'email'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
            >
              Email Settings
            </button>
            {isSuperAdmin && (
              <>
                <button
                  onClick={() => setActiveTab('auth')}
                  data-tab="auth"
                  className={`${
                    activeTab === 'auth'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
                >
                  <Shield className="h-5 w-5 mr-2" />
                  User Authorization
                </button>
                <button
                  onClick={() => setActiveTab('chatbot')}
                  data-tab="chatbot"
                  className={`${
                    activeTab === 'chatbot'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
                >
                  Chatbot Analytics
                </button>
              </>
            )}
          </nav>
        </div>
      </div>

      {activeTab === 'current-status' && <ConnectionStatusOverview />}

      {activeTab === 'calendar' && <CalendarIntegration />}
      
      {activeTab === 'tasks' && <TasksSyncIntegration />}
      
      {activeTab === 'crm' && <CRMIntegration />}
      
      {activeTab === 'risknumber' && <RiskNumberIntegration />}
      
      {activeTab === 'social' && <SocialMediaIntegration />}
      
      {activeTab === 'reviews' && <ReviewPlatformIntegration />}
      
      {activeTab === 'email' && <EmailProviderSettings />}

      {activeTab === 'auth' && isSuperAdmin && <UserAuthManager />}
      
      {activeTab === 'chatbot' && isSuperAdmin && <ChatbotAnalytics />}
    </div>
  );
};

export default Settings;